import { ReactNode } from 'react';

import dayjs from 'dayjs';

import { formatDate } from '../GeneratikTable.utils';
import { TimelineBooking } from './timeline.types';
import { unavailableStatusLabel } from './unavailability';

/**
 * What a bar says about itself before it says when it runs.
 *
 * The status comes first because it is the only place the list-view rail can show it: `TimelineStrip`
 * draws the coloured fill but has no room for the text label the grid rail puts under the bar.
 */
const leadLine = (booking: TimelineBooking, t: (key: string) => string): string | null => {
  if (booking.unavailableStatus) return unavailableStatusLabel(booking.unavailableStatus, t);
  if (booking.isExtensionSegment) return t('timeline_extension_period');
  if (booking.partial) return t('timeline_package_partially_booked');
  return null;
};

/**
 * What a timeline bar says when you hover it.
 *
 * Shared by the list rail (`TimelinePeriodCell`) and the details strip (`TimelineStrip`), which draw
 * the same bars from the same data. Held in one place because the two used to hold a copy each: the
 * same bar explaining itself differently depending on the screen is a bug nobody reports.
 *
 * A bar that has something to explain leads with it. Occupancy on a package row blocks the period
 * whether the package was sold or one of its member spaces was, and the bars are identical — so a
 * viewer looking at a package they never sold has no way to tell what happened unless the tooltip
 * says it. An unbookable row leads with its status for the same reason: nothing is booked there, so
 * the dates alone cannot explain why it is not on offer.
 *
 * A bar that knows its client says so before the dates: on a rail where several clients' bookings
 * sit side by side, the color alone is a legend without a key. Only assembly ever sets
 * `clientName`, and only for location viewers — state bars never carry it.
 */
export const barTooltipTitle = (
  booking: TimelineBooking,
  t: (key: string) => string,
): ReactNode => {
  const startFormatted = formatDate(dayjs(booking.start as Date | string).toDate());
  const endFormatted = formatDate(dayjs(booking.end as Date | string).toDate());

  const period =
    booking.status === 'UNAVAILABLE'
      ? `${t('Unavailable')}: ${startFormatted} — ${endFormatted}`
      : `${t('start_date')}: ${startFormatted} — ${t('end_date')}: ${endFormatted}`;

  const lead = leadLine(booking, t);
  const clientLine = booking.clientName ? `${t('Client')}: ${booking.clientName}` : null;

  if (!lead && !clientLine) return period;

  return (
    <>
      {lead}
      {lead && <br />}
      {clientLine}
      {clientLine && <br />}
      {period}
    </>
  );
};
